import React, { useState, useEffect, useRef } from "react"; // add useRef
import "./WiFiLab.css";

const API_URL =
  "https://op5a5iw1c1.execute-api.ap-south-1.amazonaws.com/dev/sql-injection"; // same single Lambda; path can be anything
// You’ll post with { method: "wifi", action: "..." }

const demoWordlists = {
  "Tiny (6 words)": [
    "password",
    "admin",
    "12345678",
    "qwerty123",
    "degen1234",
    "coffee@123",
  ],
  "Home (8 words)": [
    "welcome2024",
    "letmein",
    "homefiber2024",
    "iloveyou",
    "dragon",
    "superman",
    "password1",
    "Pass@123",
  ],
};

// function Terminal({ text }) {
//   return <pre ref={terminalRef} className="terminal">{text || "Ready.\n"}</pre>;
// }

export default function WiFiLab() {
  const [aps, setAps] = useState([]);
  const [selected, setSelected] = useState(null);
  const [terminal, setTerminal] = useState("");
  const [handshake, setHandshake] = useState(null);
  const [wordlistName, setWordlistName] = useState("Tiny (6 words)");

  const terminalRef = useRef(null);

  useEffect(() => {
    if (terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [terminal]);

  const callApi = async (payload) => {
    const res = await fetch(API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ method: "wifi", ...payload }),
    });

    const raw = await res.json();
    return typeof raw.body === "string" ? JSON.parse(raw.body) : raw;
  };

  const scan = async () => {
    setTerminal((t) => t + "Scanning for nearby networks...\n");
    const data = await callApi({ action: "scan" });
    setAps(data.aps || []);
    setTerminal((t) => t + (data.terminal || ""));
  };

  useEffect(() => {
    scan();
  }, []);

  const capture = async () => {
    if (!selected) return;
    setTerminal((t) => t + `Capturing handshake for ${selected.ssid}...\n`);
    const data = await callApi({ action: "capture_handshake", ...selected });
    setTerminal((t) => t + (data.terminal || ""));
    if (data.handshake) setHandshake(data.handshake);
  };

  const crack = async () => {
    if (!selected) return;
    const wl = demoWordlists[wordlistName] || [];
    setTerminal(
      (t) =>
        t + `Attempting crack with '${wordlistName}' (${wl.length} words)...\n`
    );
    const data = await callApi({
      action: "crack",
      enc: selected.enc,
      handshake,
      wordlist: wl,
    });
    setTerminal((t) => t + (data.terminal || ""));
  };

  const deauth = async () => {
    if (!selected) return;
    const data = await callApi({ action: "deauth", ...selected });
    setTerminal((t) => t + (data.terminal || ""));
  };

  const evilTwin = async () => {
    if (!selected) return;
    const data = await callApi({ action: "evil_twin", ...selected });
    setTerminal((t) => t + (data.terminal || ""));
  };

  const wps = async () => {
    if (!selected) return;
    const data = await callApi({ action: "wps_bruteforce", ...selected });
    setTerminal((t) => t + (data.terminal || ""));
  };

  return (
    <div className="wifi-container">
      <h1 className="title">🔥 WiFi Hacking Lab (Simulation)</h1>
      <p className="subtitle">
        Scan networks, capture a handshake, attempt a dictionary crack, and
        explore deauth, evil twin, and WPS scenarios — 100% simulated.
      </p>

      <div className="wifi-layout">
        <div className="left">
          <div className="toolbar">
            <button onClick={scan}>Rescan</button>
            <button onClick={capture} disabled={!selected}>
              Capture Handshake
            </button>
            <select
              value={wordlistName}
              onChange={(e) => setWordlistName(e.target.value)}
            >
              {Object.keys(demoWordlists).map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
            <button onClick={crack} disabled={!handshake}>
              Crack
            </button>
            <button onClick={deauth} disabled={!selected}>
              Deauth
            </button>
            <button onClick={evilTwin} disabled={!selected}>
              Evil Twin
            </button>
            <button onClick={wps} disabled={!selected}>
              WPS Bruteforce
            </button>
          </div>

          <div className="ap-list">
            {aps.map((ap) => (
              <div
                key={ap.bssid}
                className={`ap-card ${
                  selected?.bssid === ap.bssid ? "active" : ""
                }`}
                onClick={() => {
                  setSelected(ap);
                  setHandshake(null);
                }}
              >
                <div className="ap-ssid">{ap.ssid}</div>
                <div className="ap-meta">
                  <span>{ap.bssid}</span>
                  <span>CH {ap.channel}</span>
                  <span>{ap.enc}</span>
                  <span>WPS {ap.wps ? "ON" : "OFF"}</span>
                  <span>{ap.signal} dBm</span>
                </div>
              </div>
            ))}
          </div>

          {/* 👇 AP Detail Window */}
          {/* 👇 AP Detail Window */}
          {selected && (
            <div className="ap-details">
              <h2 className="ap-details-title">📡 Access Point Details</h2>
              <table className="ap-details-table">
                <tbody>
                  <tr>
                    <th>SSID</th>
                    <td>{selected.ssid || "Hidden"}</td>
                  </tr>
                  <tr>
                    <th>BSSID</th>
                    <td>{selected.bssid}</td>
                  </tr>
                  <tr>
                    <th>Channel</th>
                    <td>{selected.channel}</td>
                  </tr>
                  <tr>
                    <th>Encryption</th>
                    <td>{selected.enc}</td>
                  </tr>
                  <tr>
                    <th>WPS</th>
                    <td>{selected.wps ? "Enabled" : "Disabled"}</td>
                  </tr>
                  <tr>
                    <th>Signal</th>
                    <td>{selected.signal} dBm</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="right">
          {/* <Terminal text={terminal} /> */}
          <pre ref={terminalRef} className="terminal">{terminal || "Ready.\n"}</pre>
        </div>
      </div>
    </div>
  );
}
